import { useState } from "react";
import { Play, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { mediaUrl } from "@/lib/media";
import { youtubeId } from "@/lib/youtube";
import type { GalleryImage } from "@/lib/cms.types";

export function ArticleMedia({ videoUrl, gallery, title }: { videoUrl?: string | null | undefined; gallery?: GalleryImage[] | undefined; title: string }) {
  const vid = youtubeId(videoUrl);
  const images = (gallery ?? []).filter((g) => g.url);
  const [playing, setPlaying] = useState(false);
  const [open, setOpen] = useState<number | null>(null);
  if (!vid && !images.length) return null;
  const go = (d: number) => setOpen((i) => (i === null ? i : (i + d + images.length) % images.length));
  return (
    <div className="mx-auto mt-12 grid max-w-4xl gap-8">
      {vid ? (
        <div className="relative aspect-video overflow-hidden rounded-2xl border border-border bg-card">
          {playing ? (
            <iframe className="absolute inset-0 size-full" src={`https://www.youtube-nocookie.com/embed/${vid}?autoplay=1&rel=0`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          ) : (
            <button type="button" onClick={() => setPlaying(true)} aria-label={`Play video: ${title}`} className="group absolute inset-0">
              <img src={`https://i.ytimg.com/vi/${vid}/hqdefault.jpg`} alt="" loading="lazy" className="size-full object-cover" />
              <span className="absolute inset-0 grid place-items-center bg-background/20">
                <span className="grid size-16 place-items-center rounded-full bg-primary text-primary-foreground ring-2 ring-background transition-transform group-hover:scale-110">
                  <Play className="size-7 translate-x-0.5" fill="currentColor" />
                </span>
              </span>
            </button>
          )}
        </div>
      ) : null}
      {images.length ? (
        <div className={`grid gap-4 ${images.length === 1 ? "" : images.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {images.map((g, i) => (
            <figure key={i} className="grid gap-2">
              <button type="button" onClick={() => setOpen(i)} className="overflow-hidden rounded-xl border border-border">
                <img src={mediaUrl(g.url) ?? g.url} alt={g.caption || `${title} — ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform hover:scale-105" />
              </button>
              {g.caption ? <figcaption className="text-sm text-muted-foreground">{g.caption}</figcaption> : null}
            </figure>
          ))}
        </div>
      ) : null}
      <Dialog open={open !== null} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-5xl border-border bg-background p-2 [&>button]:hidden">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          {open !== null && images[open] ? (
            <div className="relative">
              <img src={mediaUrl(images[open]!.url) ?? images[open]!.url} alt={images[open]!.caption || title} className="max-h-[80vh] w-full rounded-lg object-contain" />
              {images[open]!.caption ? <p className="p-2 text-sm text-muted-foreground">{images[open]!.caption}</p> : null}
              {images.length > 1 ? (
                <>
                  <button type="button" aria-label="Previous" onClick={() => go(-1)} className="absolute top-1/2 left-2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-background/80"><ChevronLeft /></button>
                  <button type="button" aria-label="Next" onClick={() => go(1)} className="absolute top-1/2 right-2 grid size-10 -translate-y-1/2 place-items-center rounded-full bg-background/80"><ChevronRight /></button>
                </>
              ) : null}
              <button type="button" aria-label="Close" onClick={() => setOpen(null)} className="absolute top-2 right-2 grid size-9 place-items-center rounded-full bg-background/80"><X className="size-4" /></button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
